import { Injectable, NgZone, inject } from '@angular/core';
import { MatSnackBar } from '@angular/material/snack-bar';
import { SNACK_BAR_POSITION } from '../../modules_v2/shared/services/notification.service';
import { environment } from '../../../environments/environment';

/// Cada build de Angular renombra los bundles con un hash (main-3B7H3BDD.js), asi que esos se pueden
/// cachear para siempre sin riesgo. El que no lleva hash es index.html: si el navegador o la CDN lo
/// guardan, el cliente sigue pidiendo los bundles de la version anterior y no se entera de que hay una
/// nueva hasta que vacia la cache a mano.
///
/// Esto lo detecta desde dentro: se vuelve a pedir index.html saltandose la cache y se compara el
/// bundle que anuncia con el que esta corriendo. Si no coinciden, la version desplegada es otra.
/// Es un complemento de la cabecera Cache-Control del servidor, no un sustituto.
@Injectable({ providedIn: 'root' })
export class AppVersionService {
  private readonly snack = inject(MatSnackBar);
  private readonly zone = inject(NgZone);

  private readonly INTERVALO_MS = 5 * 60 * 1000;

  /// Margen para no molestar nada mas entrar: si se acaba de desplegar mientras el usuario cargaba,
  /// que termine de abrir la aplicacion antes de verse el aviso.
  private readonly ESPERA_INICIAL_MS = 60 * 1000;

  private bundleEnUso: string;
  private avisado = false;
  private comprobando = false;

  start(): void {
    // Con ng serve los bundles no llevan hash y el aviso seria ruido constante.
    if (!environment.production) return;

    this.bundleEnUso = this.bundleDelDocumento(document);

    if (!this.bundleEnUso) return;

    // Fuera de la zona de Angular: un setInterval dentro mantiene la deteccion de cambios despierta
    // cada cinco minutos para nada.
    this.zone.runOutsideAngular(() => {
      setTimeout(() => {
        this.comprobar();
        setInterval(() => this.comprobar(), this.INTERVALO_MS);
      }, this.ESPERA_INICIAL_MS);

      document.addEventListener('visibilitychange', () => {
        if (document.visibilityState === 'visible') this.comprobar();
      });
    });
  }

  private async comprobar(): Promise<void> {
    if (this.avisado || this.comprobando || !navigator.onLine) return;

    this.comprobando = true;

    try {
      const respuesta = await fetch(`index.html?v=${Date.now()}`, { cache: 'no-store' });

      if (!respuesta.ok) return;

      const desplegado = this.bundleDelHtml(await respuesta.text());

      if (!desplegado || desplegado === this.bundleEnUso) return;

      this.avisado = true;
      this.zone.run(() => this.avisar());
    } catch {
      // Sin red o con el servidor caido no hay nada que avisar: se reintenta en la siguiente vuelta.
    } finally {
      this.comprobando = false;
    }
  }

  private avisar(): void {
    const ref = this.snack.open(
      'Hay una versión nueva de Handly disponible.', 'Actualizar',
      { duration: 0, panelClass: ['snack-success'], ...SNACK_BAR_POSITION });

    ref.onAction().subscribe(() => window.location.reload());

    // Si lo cierra sin actualizar, se vuelve a ofrecer en la siguiente comprobacion.
    ref.afterDismissed().subscribe(estado => {
      if (!estado.dismissedByAction) this.avisado = false;
    });
  }

  private bundleDelDocumento(documento: Document): string {
    const scripts = Array.from(documento.querySelectorAll<HTMLScriptElement>('script[src]'));
    const main = scripts.map(script => script.getAttribute('src') || '')
      .find(src => /(^|\/)main[-.][^/]*\.js$/.test(src));

    return main || '';
  }

  private bundleDelHtml(html: string): string {
    const encontrado = html.match(/(?:src=")((?:[^"]*\/)?main[-.][^"]*\.js)"/);

    return encontrado ? encontrado[1] : '';
  }
}
